class NSSHeroCarousel extends HTMLElement {
  constructor() {
    super();

    this.debug = false;

    this.swiper;

    // Matches the theme's $medium-down breakpoint.
    this.mobileQuery = window.matchMedia(`(max-width: 989px)`);
    this.onBreakpointChange = () => this.rebuild();
  }

  connectedCallback() {
    if (this.debug) console.log("[NSS][Hero Carousel] Connected.");

    // Keep every slide so they can be swapped back in when the breakpoint changes.
    this.allSlides = [...this.querySelectorAll(`nss-hero-carousel-slide`)];
    this.filterSlides();

    this.initCarousel();
    this.initPagination();
    this.updateControlsVisibility();

    this.mobileQuery.addEventListener(`change`, this.onBreakpointChange);

    const activeDotEl = this.querySelector(`.swiper-pagination-bullet-active`);
    activeDotEl?.classList.remove(`swiper-pagination-bullet-active`);

    this.addEventListener(`click`, (e) => {
      if (e.target.hasAttribute(`nss-hero-carousel-dot-index`)) {
        const index = parseInt(e.target.getAttribute(`nss-hero-carousel-dot-index`));
        if (this.debug) console.log(`[NSS][Hero Carousel] clicked index:`, index);
        this.swiper.slideToLoop(index);
      }
    });

    // The dots are span[role="button"]; give them the Enter/Space activation a real button has.
    this.addEventListener(`keydown`, (e) => {
      if (!e.target.hasAttribute(`nss-hero-carousel-dot-index`)) return;
      if (e.key !== `Enter` && e.key !== ` `) return;
      e.preventDefault();
      e.target.click();
    });

    const editorEvents = [
      `shopify:inspector:activate`,
      `shopify:inspector:deactivate`,
      `shopify:section:load`,
      `shopify:section:unload`,
      `shopify:section:select`,
      `shopify:section:deselect`,
      `shopify:section:reorder`,
      `shopify:block:select`,
      `shopify:block:deselect`,
    ];
    editorEvents.forEach((event) => {
      this.addEventListener(event, this.handleEditorEvent.bind(this));
    });
  }

  disconnectedCallback() {
    this.mobileQuery.removeEventListener(`change`, this.onBreakpointChange);
  }

  filterSlides() {
    // Only keep slides whose "Display on" setting matches the current device.
    const device = this.mobileQuery.matches ? `mobile` : `desktop`;
    const wrapperEl = this.querySelector(`.swiper-wrapper`);
    this.allSlides.forEach((slideEl) => {
      const display = slideEl.dataset.display || `both`;
      if (display === `both` || display === device) {
        wrapperEl.appendChild(slideEl);
      } else {
        slideEl.remove();
      }
    });
    if (this.debug) console.log(`[NSS][Hero Carousel] device:`, device);
  }

  rebuild() {
    if (this.debug) console.log(`[NSS][Hero Carousel] Rebuilding for breakpoint change.`);
    this.swiper?.destroy(true, true);
    this.querySelectorAll(`.nss-hero-carousel-slide__dot`).forEach((dotEl) => dotEl.remove());
    this.querySelectorAll(
      `nss-hero-carousel-controls, .nss-hero-carousel__controls-wrapper`
    ).forEach((el) => {
      el.hidden = false;
      el.style.display = ``;
    });
    this.filterSlides();
    this.initCarousel();
    this.initPagination();
    this.updateControlsVisibility();
  }

  updatePagination(index) {
    const dotEls = this.querySelectorAll(`[nss-hero-carousel-dot-index]`);
    dotEls.forEach((dotEl) => dotEl.classList.remove(`nss-active`));
    const activeEls = this.querySelectorAll(`[nss-hero-carousel-dot-index="${index}"]`);
    activeEls.forEach((activeEl) => activeEl.classList.add(`nss-active`));
  }

  updateControlsVisibility() {
    // Hide the carousel controls (arrows + dots) when there's only one slide.
    // Counts real slides only, excluding Swiper's loop-mode duplicate clones.
    const realSlides = this.querySelectorAll(
      `nss-hero-carousel-slide:not(.swiper-slide-duplicate)`
    ).length;
    if (this.debug) console.log(`[NSS][Hero Carousel] realSlides:`, realSlides);
    if (realSlides > 1) return;
    const controlEls = this.querySelectorAll(
      `nss-hero-carousel-controls, .nss-hero-carousel__controls-wrapper`
    );
    controlEls.forEach((el) => {
      el.hidden = true;
      el.style.display = `none`;
    });
  }

  initPagination() {
    const paginationEls = this.querySelectorAll(`[nss-hero-carousel-pagination]`);
    const slidesCount = paginationEls.length;
    if (this.debug) console.log(`[NSS][Hero Carousel] slidesCount:`, slidesCount);
    paginationEls.forEach((paginationEl) => {
      for (let i = 0; i < slidesCount; i++) {
        const dotEl = document.createElement(`span`);
        dotEl.classList.add(`nss-hero-carousel-slide__dot`);
        if (i === 0) {
          dotEl.classList.add(`nss-active`);
        }
        dotEl.setAttribute(`nss-hero-carousel-dot-index`, i);
        dotEl.setAttribute(`tabindex`, 0);
        dotEl.setAttribute(`role`, `button`);
        dotEl.setAttribute(`aria-label`, `Go to slide ${i + 1}`);
        paginationEl.appendChild(dotEl);
      }
    });
  }

  initCarousel() {
    const carouselEl = this.querySelector(`.swiper`);
    const settingsEl = document.querySelector(
      `script[data-section-id="${this.dataset.sectionId}"]`
    );
    if (this.debug)
      console.log(`[NSS][Hero Carousel] settingsEl.textContent:`, settingsEl.textContent);
    const settings = JSON.parse(settingsEl.textContent);
    if (this.debug) console.log(`[NSS][Hero Carousel] settings:`, settings);

    this.swiper = new Swiper(
      carouselEl,
      Object.assign({}, settings, {
        grabCursor: true,
        slidesPerView: 1,

        a11y: {
          enabled: true,
        },

        navigation: {
          prevEl: ".nss-hero-carousel-prev",
          nextEl: ".nss-hero-carousel-next",
        },
        pagination: {
          el: ".swiper-pagination",
          type: "bullets",
          clickable: true,
        },

        on: {
          realIndexChange: () => {
            if (this.debug)
              console.log(`[NSS][Hero Carousel] this.swiper.realIndex:`, this.swiper.realIndex);
            this.updatePagination(this.swiper.realIndex);
          },
        },
      })
    );
  }

  handleEditorEvent(e) {
    if (this.debug) console.log("[NSS][Hero Carousel] Handle editor event, e:", e);

    switch (e.type) {
      case `shopify:block:select`:
        const blockEl = this.querySelector(`[data-block-id="${e.detail.blockId}"]`);
        // The block may belong to a slide hidden on the current device.
        if (!blockEl) break;
        const slideEl = blockEl.closest(`nss-hero-carousel-slide`);
        const index = slideEl.getAttribute(`data-swiper-slide-index`);
        if (this.debug) console.log("[NSS][Hero Carousel] Sliding to index:", index);
        this.swiper.slideTo(index);
        break;
    }
  }
}

let elementName = `nss-hero-carousel`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSHeroCarousel);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`);
}
